import { Component, ComponentRef, EventEmitter, Input, OnDestroy, OnInit, Output, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { PaymentMethodViewModel } from 'libs/core/src/lib/model/view-model/order/payment-method/payment-method.view.model';
import { PaymentViewModel } from 'libs/core/src/lib/model/view-model/payment.view.model';
import { Observable, Subscription } from 'rxjs';
import { PaymentProviderStateService } from '../service/payment-provider-state.service';
import { PaymentProviderEvent } from './event/payment-provider.event';
import { PaymentProviderResolver } from './provider/payment-provider.resolver';
import { OrderPaymentPackage } from '../../../../../../core/src/lib/model/order-payment.package.model';
import { PaymentProviderComponentInterface } from './provider/payment-provider.component.interface';
import { PaymentProviderStateEnum } from './model/payment-provider-status.enum';

@Component({
  selector: 'app-payment-proxy-component',
  templateUrl: './payment-proxy.component.html',
  standalone: false
})
export class PaymentProxyComponent implements OnInit, OnDestroy {
  @ViewChild('providerContainer', { read: ViewContainerRef, static: true })
  public providerContainer: ViewContainerRef;

  @Input() public paymentMethod: PaymentMethodViewModel;
  @Output() public providerEvent = new EventEmitter<PaymentProviderEvent>();
  @Output() public hideLoader = new EventEmitter();
  @Output() public showLoader = new EventEmitter();
  @Output() public providerFormValidChange = new EventEmitter<boolean>();
  @Output() public providerCardMetadataChange = new EventEmitter<unknown>();

  public componentRef: ComponentRef<PaymentProviderComponentInterface>;
  private eventsSubscription: Subscription = Subscription.EMPTY;

  public get paymentInitialized(): boolean {
    return this.paymentProviderStateService.getProperComponent(this.paymentMethod?.identifier)?.instance?.paymentInitialized;
  }

  public get paymentInProgress(): boolean {
    return (
      this.paymentProviderStateService.getProperComponent(this.paymentMethod?.identifier)?.instance?.paymentProviderState ===
      PaymentProviderStateEnum.InProgress
    );
  }

  public constructor(
    private providerComponentFactory: PaymentProviderResolver,
    private paymentProviderStateService: PaymentProviderStateService
  ) {}

  public ngOnInit(): void {
    this.showLoader.emit();
    this.createComponent(this.paymentMethod?.identifier);
  }

  public ngOnDestroy(): void {
    this.destroyComponent();
  }

  public onInitPayment(paymentMethod: PaymentMethodViewModel): Observable<any> {
    return this.paymentProviderStateService.getProperComponent(paymentMethod?.identifier).instance.onInitPayment(paymentMethod);
  }

  public onPostInitPayment(paymentModel: PaymentViewModel, identifier: string): void {
    this.paymentProviderStateService.getProperComponent(identifier).instance.onPostInitPayment(paymentModel);
  }

  public onPreInitPayment(paymentModel: OrderPaymentPackage): Observable<OrderPaymentPackage> {
    return this.paymentProviderStateService.getProperComponent(paymentModel.paymentProviderIdentifier).instance.onPreInitPayment(paymentModel);
  }

  private createComponent(providerType: string): void {
    const componentType: Type<PaymentProviderComponentInterface> = this.providerComponentFactory.getComponentType(providerType);

    if (componentType) {
      const componentRef: ComponentRef<PaymentProviderComponentInterface> = this.providerContainer.createComponent(componentType);
      this.eventsSubscription = componentRef.instance.events.subscribe((x) => {
        this.providerEvent.next(x);
      });

      if (componentRef?.instance?.formValidChange) {
        this.eventsSubscription.add(
          componentRef.instance.formValidChange.subscribe((x) => {
            this.providerFormValidChange.next(x);
          })
        );
      }

      if (componentRef?.instance?.cardMetadataChange) {
        this.eventsSubscription.add(
          componentRef.instance.cardMetadataChange.subscribe((x) => {
            this.providerCardMetadataChange.next(x);
          })
        );
      }

      this.paymentProviderStateService.addAvailablePaymentProvider(componentRef, providerType);
      this.registerComponent(componentRef);
    } else {
      throw new Error(`Unknown provider type '${providerType}'`);
    }
  }

  private registerComponent(componentRef: ComponentRef<PaymentProviderComponentInterface>): void {
    this.componentRef = componentRef;
    this.providerContainer.insert(componentRef.hostView);
    this.hideLoader.emit();
  }

  private destroyComponent(): void {
    if (this.eventsSubscription !== Subscription.EMPTY) {
      this.eventsSubscription.unsubscribe();
    }
    this.eventsSubscription = Subscription.EMPTY;
    this.componentRef.destroy();
  }
}
